// @givmo/app-admin — code-based TanStack Router route tree (07-1.0 scaffold).
//
// Two areas, mirroring the Flutter webapp's BeamGuard split:
//   - UNAUTHENTICATED: `/login-home` — a minimal placeholder (the real provider-chooser + email/
//     register/OTP screens are 07-1.1). The guard redirects unauthenticated users here.
//   - AUTHENTICATED (behind the `_authenticated` pathless layout + `beforeLoad` guard): the sidebar
//     destinations — /dashboard, /approvals, /employees, /admins, /reports, /settings, /support. Default
//     authenticated landing is /dashboard. (/reporting and /documents are retired — both redirect
//     to /reports, which absorbed the annual-report PDF surface in EP-DES2 Wave 0 — and so is /fees,
//     which redirects to /dashboard since EP-WEB37 retired the fee page.)
//
// The guard (parity with the Flutter `BeamGuard`, which only checks `currentUser != null`) reads
// `context.auth.isAuthenticated`; a signed-out user hitting any authenticated route is redirected to
// /login-home with a `redirect` search param (so a later slice can bounce them back post-login).
//
// Routing is CODE-BASED (no @tanstack/router-plugin codegen) — a deliberate scaffold choice that
// keeps the route tree explicit and trivially unit-testable with an in-memory router.

import { createRootRouteWithContext, createRoute, Outlet, redirect } from '@tanstack/react-router';
import { lazy, Suspense } from 'react';
import { Toaster } from '@givmo/design-system';
import { AppShell } from '../components/app-shell';
import { SignInMainScreen } from '../features/auth/sign-in-main-screen';
import { SignInScreen } from '../features/auth/sign-in-screen';
import { SignUpMainScreen } from '../features/auth/sign-up-main-screen';
import { SignUpDetailsScreen } from '../features/auth/sign-up-details-screen';
import { SignUpEmailPasswordScreen } from '../features/auth/sign-up-email-password-screen';
import { SignUpOtpScreen } from '../features/auth/sign-up-otp-screen';
import { SignUpVerifiedScreen } from '../features/auth/sign-up-verified-screen';
import { SignUpRequestAccessScreen } from '../features/auth/sign-up-request-access-screen';
import { PortalSignUpHost } from '../features/auth/portal-sign-up-host';
import { LostCompanyAccessScreen } from '../features/auth/lost-company-access-screen';
import {
  OrganizationSsoCallbackScreen,
  OrganizationSsoScreen,
} from '../features/auth/organization-sso-screen';
import { ORGANIZATION_SSO_CALLBACK_PATH } from '@givmo/passkeys';
import { AUTH_ROUTES } from '../features/auth/auth-routing';
import { sanitizeRedirectTarget } from '@givmo/org-console';
import { landSignedInVisitor } from '../features/auth/companyless-landing';
import { useCompanylessLanding } from '../features/auth/use-companyless-landing';
import {
  OnboardingMatchPolicyRoute,
  useOnboardingRedirect,
} from '../features/onboarding/onboarding-route';
import { SettingsPage } from '../features/settings/settings-page';
import { MatchProgramPage } from '../features/match-program/match-program-page';
import { SsoTestCallbackPage } from '../features/security/sso-test-callback-page';
import { SSO_TEST_CALLBACK_PATH } from '../features/security/sso-test-session';
import { DashboardPage } from '../features/dashboard/dashboard-page';
import { ApprovalsPage } from '../features/approvals/approvals-page';
import { GivePage } from '@givmo/employer-module/features/give/give-page';
import { EmployeesPage, type EmployeesTab } from '../features/employees/employees-page';
import { ReportsPage } from '../features/reports/reports-page';
import { AdminsPage } from '../features/admins/admins-page';
import { SupportPage } from '../features/support/support-page';
import {
  SIGN_IN_SECURITY_ROUTE,
  SignInSecurityPage,
} from '../features/sign-in-security/sign-in-security-page';
import { CompanyFundraisersPage } from '../features/company-fundraisers/company-fundraisers-page';
import { CompanyFundraiserWizard } from '../features/company-fundraisers/company-fundraiser-wizard';
import { CompanyFundraiserDetailPage } from '../features/company-fundraisers/company-fundraiser-detail-page';
import { GiftCardsPage } from '../features/gift-cards/gift-cards-page';
import { CompetitionsPage } from '../features/competitions/competitions-page';
import { CompetitionFormPage } from '../features/competitions/competition-form-page';
import { CompetitionDetailPage } from '../features/competitions/competition-detail-page';
import { parseGiveSearch } from '@givmo/employer-module/features/give/give-decode';
import {
  isCompanyDirectGivingEnabled,
  isCompanyFundraisersEnabled,
  isCompanyGiftCardsEnabled,
  isCompanyStepsCompetitionsEnabled,
} from '../lib/env';
import type { AdminRouterContext } from './context';

/** Parse the optional `redirect` (post-auth bounce target) off a route's search params. */
function parseRedirectSearch(search: Record<string, unknown>): { redirect?: string } {
  const redirect = typeof search.redirect === 'string' ? search.redirect : undefined;
  return redirect ? { redirect } : {};
}

/** Devtools are dev-only and code-split so they never ship in the production bundle. */
const RouterDevtools = import.meta.env.PROD
  ? () => null
  : lazy(() =>
      import('@tanstack/react-router-devtools').then((m) => ({
        default: m.TanStackRouterDevtools,
      })),
    );

// ── Root ───────────────────────────────────────────────────────────────────────────────────────
export const rootRoute = createRootRouteWithContext<AdminRouterContext>()({
  component: function RootComponent() {
    return (
      <>
        <Outlet />
        {/* App-wide toast host (the onboarding wizard + later mutation surfaces toast here). */}
        <Toaster />
        <Suspense fallback={null}>
          <RouterDevtools />
        </Suspense>
      </>
    );
  },
});

// ── Index: send `/` to the default authenticated landing (/dashboard). The `_authenticated` guard
//    then redirects to /login-home if the user is signed out. ───────────────────────────────────
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/dashboard' });
  },
});

// ── Unauthenticated area (07-1.1a) — auth screens as siblings off the root (NOT behind the
//    authenticated layout). The guard redirects unauthenticated users to /login-home (the sign-in
//    main chooser); the screens map the @givmo/auth routing state onto these routes. ──────────────

// /login-home — sign-in MAIN chooser (Email / Google / Microsoft). KEEPS the `page-login-home`
// testid (the guard tests assert it). `redirect` carries the guard's intended post-login target.
const loginHomeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login-home',
  validateSearch: parseRedirectSearch,
  beforeLoad: landSignedInArrival,
  component: SignInMainScreen,
});

// /sign-in — email/password form. `redirect` (post-login bounce) + `redirect-block` (show the
// "already created an account on the Givmo mobile app" magenta block, the Flutter `isRedirect`).
const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in',
  validateSearch: (
    search: Record<string, unknown>,
  ): { redirect?: string; 'redirect-block'?: boolean } => ({
    ...parseRedirectSearch(search),
    ...(search['redirect-block'] === true || search['redirect-block'] === 'true'
      ? { 'redirect-block': true }
      : {}),
  }),
  beforeLoad: landSignedInArrival,
  component: SignInScreen,
});

// /sign-up — the chooser (work email / Google / Microsoft + Terms/Privacy warning).
//
// EP-SUITE2-FIX4 + EP-SUITE2-FIX5 ⚠ A SIGNED-IN VISITOR WHO ARRIVES AT A DOOR IS LANDED, NOT SHOWN IT.
// A door's ceremony swaps or drops the session it starts from: the provider sign-UP's existing-account
// arm signs that account in and back OUT, a provider sign-IN with a new account rolls that account
// back, and a failed email registration deletes the account it just created — each locally only, so a
// signed-in admin who ran one was left with no local session beside their own still-live suite cookie
// (Account Security's cut-0922b review, P2; cut employer-portal-6's review, OOS-2). So the four doors
// that start one — `/login-home`, `/sign-in`, `/sign-up`, `/sign-up/email-password` — send a signed-in
// ARRIVAL where they belong (the sibling consoles' door shape, charity's
// `redirectAuthenticatedFromPublicAuth`), by one read (`landSignedInVisitor`):
//   - their company read SUCCEEDS → the portal (`/dashboard`), which then starts warm;
//   - it answers the company-less 403 → the funnel screen their routing state names
//     (`companyless-landing.ts`) — the portal would show them nothing but "Permission denied";
//   - no funnel screen fits, or the read fails for any other reason → they stay: the direction that
//     changes nothing. Asked once (`retry: false`): a 403 is definitive, and a retry only delays.
// ARRIVALS only (`cause` `'enter'` or `'preload'`). A ceremony signs its account in ON the door, and
// the identity boundary's `router.invalidate()` and keyed remount (`app.tsx`) re-run the guard where
// it stands with cause `'stay'`; landing then would race the ceremony's own navigation.
// A signed-out visitor returns synchronously, before any read: unchanged.
// A full page load arrives ONCE: the SDK's session restore is the router's first identity, which the
// boundary does not reset (EP-SUITE2-FIX7), so the redirect is thrown while the door is still
// pending. Thrown at a door that has already rendered, it is one TanStack Router cannot suspend.
function landSignedInArrival({
  context,
  cause,
}: {
  context: AdminRouterContext;
  cause: 'preload' | 'enter' | 'stay';
}) {
  if (cause === 'stay' || context.auth.user === null) return;
  return landArrival(context.queryClient, context.auth.user);
}

async function landArrival(
  queryClient: AdminRouterContext['queryClient'],
  user: NonNullable<AdminRouterContext['auth']['user']>,
): Promise<void> {
  const landing = await landSignedInVisitor(queryClient, user);
  if (landing !== null) throw redirect(landing);
}

// The funnel's screens live in `@givmo/employer-module` since EP-MNT8 (the company console renders
// them too); `PortalSignUpHost` hands them this app's auth, `AUTH_ROUTES`, its look and its frame.
const signUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-up',
  beforeLoad: landSignedInArrival,
  component: function SignUpRoute() {
    return (
      <PortalSignUpHost>
        <SignUpMainScreen />
      </PortalSignUpHost>
    );
  },
});

// /lost-company-access — the way back in for someone whose company locks how they sign in
// (AS-WEB26): the chooser's "Lost access to your company sign-in?" opens it. Signed out by nature, so
// a signed-in arrival is landed as at every door. `back` is the door to return to (sanitized).
const lostCompanyAccessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: AUTH_ROUTES.lostCompanyAccess,
  validateSearch: (search: Record<string, unknown>): { back?: string } => {
    const back = sanitizeRedirectTarget(search.back);
    return back ? { back } : {};
  },
  beforeLoad: landSignedInArrival,
  component: LostCompanyAccessScreen,
});

// /sso — the organization door's step (AS-WEB30): the chooser's "Sign in with Organization SSO" opens
// it. A door, so a signed-in arrival is landed as at every door. `redirect` is where signing in lands;
// `back` the door to return to (sanitized).
const organizationSsoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: AUTH_ROUTES.organizationSso,
  validateSearch: (search: Record<string, unknown>): { redirect?: string; back?: string } => {
    const back = sanitizeRedirectTarget(search.back);
    return { ...parseRedirectSearch(search), ...(back ? { back } : {}) };
  },
  beforeLoad: landSignedInArrival,
  component: OrganizationSsoScreen,
});

// /sso/callback — where the employer's sign-in page sends the browser back, with a one-use handoff
// code or an error (AS-WEB30).
// ⚠ THIS PATH IS AN OPERATOR CONTRACT. Joined to each environment's portal origin (both prod hosts)
// it is what is registered in the backend's `EMPLOYER_SSO_ALLOWED_REDIRECT_URIS`, matched EXACTLY.
// NO signed-in guard, deliberately: the sign-in this page finishes changes the identity, and the
// page lands the person itself (its routing state, or the companyless landing). A bare visit goes
// on to `/sso`, whose own guard lands a signed-in visitor.
const organizationSsoCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ORGANIZATION_SSO_CALLBACK_PATH,
  validateSearch: (search: Record<string, unknown>): { handoff_code?: string; error?: string } => ({
    ...(typeof search.handoff_code === 'string' ? { handoff_code: search.handoff_code } : {}),
    ...(typeof search.error === 'string' ? { error: search.error } : {}),
  }),
  component: OrganizationSsoCallbackScreen,
});

// The sign-up multi-step subtree. Neither the collected company details nor the EP-SUITE2
// join-or-create pivot target is a provider here: the email path creates the Firebase user mid-flow,
// and the uid change remounts this whole tree (`app.tsx`, the identity boundary), so both live in
// module-level stores (`sign-up-context.tsx`, `sign-up-join-context.tsx`) that the remount cannot
// reset — the details since NF-FIX21, when the code screen (after that remount) took over the
// registration the email/password screen used to make before the account existed.
const signUpFlowLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'sign-up-flow',
  component: function SignUpFlowLayout() {
    return (
      <PortalSignUpHost>
        <Outlet />
      </PortalSignUpHost>
    );
  },
});

// `provider` is the EXPLICIT provider-registration signal (the Flutter `isProviderAuth`): `true`
// ONLY on the two provider/unregistered navigations to details. Without it, the screen takes the
// email path — so a back-navigated, already-registered email user (still authenticated) is NOT
// misclassified as a provider and does NOT re-trigger a duplicate company create.
const signUpDetailsRoute = createRoute({
  getParentRoute: () => signUpFlowLayoutRoute,
  path: '/sign-up/details',
  validateSearch: (search: Record<string, unknown>): { provider?: boolean } =>
    search.provider === true || search.provider === 'true' ? { provider: true } : {},
  component: function SignUpDetailsRoute() {
    const { provider } = signUpDetailsRoute.useSearch();
    return <SignUpDetailsScreen provider={provider} />;
  },
});

const signUpEmailPasswordRoute = createRoute({
  getParentRoute: () => signUpFlowLayoutRoute,
  path: '/sign-up/email-password',
  // A door that CREATES an account (see `landSignedInArrival`): a signed-in visitor is landed.
  beforeLoad: landSignedInArrival,
  component: SignUpEmailPasswordScreen,
});

const signUpOtpRoute = createRoute({
  getParentRoute: () => signUpFlowLayoutRoute,
  path: '/sign-up/otp',
  // `register` marks the EMAIL sign-up's verification (NF-FIX21): the account exists with its row and
  // NO company, and post-confirm the screen registers the company from the details carried over from
  // the first screen. `provider` marks a verification arrival with no company and no details to
  // register (microsoft.com verified BEFORE any company exists, or an email account that stopped at
  // the code); post-confirm the screen re-resolves the route instead of assuming the pending room.
  // `join` marks a join-or-create requester's verification (EP-SUITE2-FIX2): post-confirm the screen
  // lands on `/sign-up/request-access` — never the pending room. `provider` wins over the others when
  // several are present (the OAuth path is unchanged), then `join`.
  validateSearch: (
    search: Record<string, unknown>,
  ): { provider?: boolean; join?: boolean; register?: boolean } => ({
    ...(search.provider === true || search.provider === 'true' ? { provider: true } : {}),
    ...(search.join === true || search.join === 'true' ? { join: true } : {}),
    ...(search.register === true || search.register === 'true' ? { register: true } : {}),
  }),
  component: function SignUpOtpRoute() {
    const { provider, join, register } = signUpOtpRoute.useSearch();
    return <SignUpOtpScreen provider={provider} join={join} register={register} />;
  },
});

const signUpVerifiedRoute = createRoute({
  getParentRoute: () => signUpFlowLayoutRoute,
  path: '/sign-up/verified',
  component: SignUpVerifiedScreen,
});

// EP-SUITE2 — the join-or-create request-access screen (flag-gated; reached only when a resolve /
// create-fork determined the caller's work-email domain already belongs to an ACTIVE company).
const signUpRequestAccessRoute = createRoute({
  getParentRoute: () => signUpFlowLayoutRoute,
  path: '/sign-up/request-access',
  component: SignUpRequestAccessScreen,
});

// ── Authenticated route guard (shared) ──────────────────────────────────────────────────────────
// Parity with the Flutter `BeamGuard` (currentUser != null): a signed-out user hitting any
// authenticated route is redirected to /login-home with the intended target carried as `redirect`.
const requireAuthBeforeLoad = ({
  context,
  location,
}: {
  context: AdminRouterContext;
  location: { href: string };
}) => {
  if (!context.auth.isAuthenticated) {
    throw redirect({ to: '/login-home', search: { redirect: location.href } });
  }
};

// ── Authenticated layout (pathless) + route guard ───────────────────────────────────────────────
// Hosts the 6 sidebar destinations inside the <AppShell> chrome, and owns the onboarding trigger:
// a fresh admin (no per-employee budget + not dismissed on this device) is redirected into the
// match-policy wizard. A budgeted company is never redirected.
const authenticatedLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authenticated',
  beforeLoad: requireAuthBeforeLoad,
  component: function AuthenticatedLayout() {
    useOnboardingRedirect();
    // EP-SUITE2-FIX5: a signed-in caller with no company is replaced onto their funnel screen.
    useCompanylessLanding();
    return (
      <AppShell>
        <Outlet />
      </AppShell>
    );
  },
});

// ── Onboarding (07-1.1b): a SEPARATE authenticated layout WITHOUT the AppShell chrome — the
//    match-policy wizard is full-screen (parity with the Flutter `OnboardingScreen`, its own
//    MainAppBar + no nav sidebar). Same auth guard; reached via the layout redirect above. The
//    route component re-checks the gate and bounces a budgeted/dismissed admin back to /dashboard.
const onboardingLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'onboarding',
  beforeLoad: requireAuthBeforeLoad,
  component: function OnboardingLayout() {
    useCompanylessLanding();
    return <Outlet />;
  },
});

const onboardingMatchPolicyRoute = createRoute({
  getParentRoute: () => onboardingLayoutRoute,
  path: '/onboarding/match-policy',
  component: OnboardingMatchPolicyRoute,
});

// ── The authenticated destinations (parity order/labels) ────────────────────────────────────────
const dashboardRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/dashboard',
  component: DashboardPage,
});

// /approvals (EP-DES1 §1/§2) — the full match-approval queue, split out of the dashboard in Wave 1.
// NOT flag-gated: employer match approval is the portal's oldest live capability, so this is a new
// address for a shipped surface rather than a dark one. The dashboard keeps a Needs-attention summary
// with the same Approve/Decline actions; see approvals-page.tsx.
const approvalsRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/approvals',
  component: ApprovalsPage,
});

// /give (EP-WEB1) — the company DIRECT-GIVING create flow, gated by the dark switch. The parent
// `authenticatedLayoutRoute.beforeLoad` enforces auth first; this beforeLoad redirects to /dashboard
// while `VITE_COMPANY_DIRECT_GIVING_ENABLED` is off, so the flow is UNREACHABLE (even by a typed URL)
// until the switch flips in lockstep with the backend `COMPANY_FUNDING_SOURCE_ROUTING_ENABLED` flag.
// The give flow accepts an optional `?fundraiserId=` PRESELECT (the EP-WEB3 "Seed this fundraiser" CTA
// links here pre-targeted to a company fundraiser). The param is validated by `parseGiveSearch` — the
// pure, unit-tested parser in give-decode (junk/blank values are dropped) — and passed to the page;
// absent, the flow behaves exactly as before (interactive target pick).
const giveRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/give',
  validateSearch: parseGiveSearch,
  beforeLoad: () => {
    if (!isCompanyDirectGivingEnabled()) {
      throw redirect({ to: '/dashboard' });
    }
  },
  component: function GiveRouteComponent() {
    const { fundraiserId } = giveRoute.useSearch();
    return <GivePage preselectFundraiserId={fundraiserId ?? null} />;
  },
});

// ── Company Fundraisers (EP-WEB3) — the company's own fundraiser create/manage area, gated by the
//    `VITE_COMPANY_FUNDRAISERS_ENABLED` dark switch. The parent enforces auth; each beforeLoad redirects
//    to /dashboard while the switch is off, so the area is UNREACHABLE (even by a typed URL) until it
//    flips in lockstep with the backend `COMPANY_FUNDRAISER_CREATION_ENABLED` flag.
const requireCompanyFundraisersEnabled = () => {
  if (!isCompanyFundraisersEnabled()) {
    throw redirect({ to: '/dashboard' });
  }
};

const companyFundraisersRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/company-fundraisers',
  beforeLoad: requireCompanyFundraisersEnabled,
  component: CompanyFundraisersPage,
});

const companyFundraiserNewRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/company-fundraisers/new',
  beforeLoad: requireCompanyFundraisersEnabled,
  component: CompanyFundraiserWizard,
});

const companyFundraiserDetailRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/company-fundraisers/$fundraiserId',
  beforeLoad: requireCompanyFundraisersEnabled,
  component: function CompanyFundraiserDetailRouteComponent() {
    const { fundraiserId } = companyFundraiserDetailRoute.useParams();
    return <CompanyFundraiserDetailPage fundraiserId={fundraiserId} />;
  },
});

// /gift-cards (EP-WEB4) — the company GIFT CARDS (employer gifting v1) area, gated by the dark switch.
// The parent `authenticatedLayoutRoute.beforeLoad` enforces auth first; this beforeLoad redirects to
// /dashboard while `VITE_COMPANY_GIFT_CARDS_ENABLED` is off, so the area is UNREACHABLE (even by a
// typed URL) until the switch flips in lockstep with the backend `COMPANY_GIFT_CARDS_ENABLED` flag.
const giftCardsRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/gift-cards',
  beforeLoad: () => {
    if (!isCompanyGiftCardsEnabled()) {
      throw redirect({ to: '/dashboard' });
    }
  },
  component: GiftCardsPage,
});

// ── Steps Competitions (EP-WEB10) — the company's walking-competition area, gated by the
//    `VITE_COMPANY_STEPS_COMPETITIONS_ENABLED` dark switch. The parent enforces auth; each beforeLoad
//    redirects to /dashboard while the switch is off, so the area is UNREACHABLE (even by a typed URL)
//    until it flips alongside the backend `STEPS_COMPETITIONS_ENABLED` flag for that env.
const requireCompetitionsEnabled = () => {
  if (!isCompanyStepsCompetitionsEnabled()) {
    throw redirect({ to: '/dashboard' });
  }
};

const competitionsRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/competitions',
  beforeLoad: requireCompetitionsEnabled,
  component: CompetitionsPage,
});

const competitionNewRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/competitions/new',
  beforeLoad: requireCompetitionsEnabled,
  component: function CompetitionNewRouteComponent() {
    return <CompetitionFormPage competitionId={null} />;
  },
});

const competitionDetailRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/competitions/$competitionId',
  beforeLoad: requireCompetitionsEnabled,
  component: function CompetitionDetailRouteComponent() {
    const { competitionId } = competitionDetailRoute.useParams();
    return <CompetitionDetailPage competitionId={competitionId} />;
  },
});

const competitionEditRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/competitions/$competitionId/edit',
  beforeLoad: requireCompetitionsEnabled,
  component: function CompetitionEditRouteComponent() {
    const { competitionId } = competitionEditRoute.useParams();
    return <CompetitionFormPage competitionId={competitionId} />;
  },
});

// /match-program (EP-MNT2) — Giving › Match Program: the policy builder, the rendered policy and a link
// to Approvals (the year's utilization stays the dashboard's card, per the mockup ruling). Always on
// (no switch): it is where the match policy is edited now that it has left Settings.
const matchProgramRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/match-program',
  component: MatchProgramPage,
});

// /settings — `?tab=<id>` deep-links a settings tab (AS-WEB6), the same shape `/employees` uses. It
// is what the SSO test-connection landing page returns to, so a ceremony that took the Owner's
// browser to their identity provider and back lands on the tab they left. The registry tolerates an
// unknown id (it resolves to the first visible tab), so nothing validates the value against a list
// of tabs here — the router must not know which tabs exist.
//
// ONE exception, and it is an address rather than a tab: `?tab=match` names the match policy, which
// left Settings for its own page at EP-MNT2. A bookmark or a delivered link carrying it REPLACES to
// `/match-program` instead of falling through to the first remaining tab.
const settingsRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/settings',
  validateSearch: (search: Record<string, unknown>): { tab?: string } =>
    typeof search.tab === 'string' && search.tab.length > 0 ? { tab: search.tab } : {},
  beforeLoad: ({ search }) => {
    if (search.tab === 'match') throw redirect({ to: '/match-program', replace: true });
  },
  component: function SettingsRouteComponent() {
    const { tab } = settingsRoute.useSearch();
    return <SettingsPage initialTab={tab} />;
  },
});

// /settings/security/sso/test-callback (AS-WEB6) — where the backend's 303 lands a finished SSO test
// connection. A ROOT route, NOT behind `_authenticated`, and the reasoning is in the page's own
// header: it is usually a pop-up whose only job is to close itself, so bouncing it to /login-home on
// a momentarily-absent session would flash a sign-in screen on the way to nothing — and there is
// nothing to protect, since it renders two values out of its own URL and calls no endpoint.
//
// ⚠ THIS PATH IS AN OPERATOR CONTRACT. Joined to each environment's admin origin it is what is
// registered in the backend's `EMPLOYER_SSO_ALLOWED_REDIRECT_URIS`, matched EXACTLY. Renaming it
// silently breaks the ceremony in every environment until an operator re-registers it.
const ssoTestCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: SSO_TEST_CALLBACK_PATH,
  validateSearch: (search: Record<string, unknown>): { sso_test?: string; error?: string } => ({
    ...(typeof search.sso_test === 'string' ? { sso_test: search.sso_test } : {}),
    ...(typeof search.error === 'string' ? { error: search.error } : {}),
  }),
  component: function SsoTestCallbackRouteComponent() {
    const search = ssoTestCallbackRoute.useSearch();
    return <SsoTestCallbackPage ssoTest={search.sso_test} error={search.error} />;
  },
});

// /employees (EP-WEB8) — Roster + Invites as two tabs. `?tab=invites` deep-links the Invites tab so the
// dashboard's Participation panel and the utilization card's empty state can send an admin straight to
// the loop that fixes what they were just looking at. Anything other than the two known values falls
// back to Roster: an unrecognized tab must not render a blank surface.
const employeesRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/employees',
  validateSearch: (search: Record<string, unknown>): { tab?: EmployeesTab } =>
    search.tab === 'invites' || search.tab === 'roster' ? { tab: search.tab } : {},
  component: function EmployeesRouteComponent() {
    const { tab } = employeesRoute.useSearch();
    return <EmployeesPage initialTab={tab ?? 'roster'} />;
  },
});

// /reports (EP-DES2 Wave 0) — the one destination for everything a company hands to finance. Today
// it renders only its "Tax documents" section (the annual-report PDFs); Recap + Exports arrive with
// EP-WEB7. See reports-page.tsx.
const reportsRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/reports',
  component: ReportsPage,
});

// Both former surfaces redirect into /reports, so no bookmark or post-login `redirect` target ever
// 404s. The chain is deliberately FLATTENED rather than hopped (`/reporting` → `/documents` →
// `/reports`): each redirects straight to the destination, so an old /reporting bookmark resolves in
// one step.
//
// /reporting was retired in 07-3b gap ③ (the Flutter webapp's Reporting screen — generate the annual
// report — was absorbed into the Documents surface). /documents was retired in Wave 0: "Documents"
// meant exactly one thing (tax PDFs) and cannot host exports and a recap without becoming a misnomer
// (spec §1 + §8 Q4, approved).
const reportingRedirectRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/reporting',
  beforeLoad: () => {
    throw redirect({ to: '/reports' });
  },
});

const documentsRedirectRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/documents',
  beforeLoad: () => {
    throw redirect({ to: '/reports' });
  },
});

// /fees was the fee page (EP-WEB12), retired by EP-WEB37 (D-EP-59): every payment dialog quotes its
// own fee before it charges, so the page took no successor. Prod served it from 2026-09-12, opened in
// a new tab from the money dialogs, Support and Settings, so the address lives on in bookmarks, open
// tabs and post-login `redirect` targets. It lands on /dashboard, where it always sent anyone while
// its switch was off.
const feesRedirectRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/fees',
  beforeLoad: () => {
    throw redirect({ to: '/dashboard' });
  },
});

const adminsRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/admins',
  component: AdminsPage,
});

const supportRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/support',
  component: SupportPage,
});

// AS-WEB11: the person's own Sign-in & security page, opened from the account menu (never a
// settings tab — that strip is the company's).
const signInSecurityRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: SIGN_IN_SECURITY_ROUTE,
  component: SignInSecurityPage,
});

export const routeTree = rootRoute.addChildren([
  indexRoute,
  loginHomeRoute,
  signInRoute,
  signUpRoute,
  lostCompanyAccessRoute,
  organizationSsoRoute,
  organizationSsoCallbackRoute,
  ssoTestCallbackRoute,
  signUpFlowLayoutRoute.addChildren([
    signUpDetailsRoute,
    signUpEmailPasswordRoute,
    signUpOtpRoute,
    signUpVerifiedRoute,
    signUpRequestAccessRoute,
  ]),
  authenticatedLayoutRoute.addChildren([
    dashboardRoute,
    approvalsRoute,
    giveRoute,
    companyFundraisersRoute,
    companyFundraiserNewRoute,
    companyFundraiserDetailRoute,
    giftCardsRoute,
    competitionsRoute,
    competitionNewRoute,
    competitionDetailRoute,
    competitionEditRoute,
    matchProgramRoute,
    settingsRoute,
    employeesRoute,
    reportsRoute,
    reportingRedirectRoute,
    documentsRedirectRoute,
    feesRedirectRoute,
    adminsRoute,
    supportRoute,
    signInSecurityRoute,
  ]),
  onboardingLayoutRoute.addChildren([onboardingMatchPolicyRoute]),
]);
